import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import type { AuthCaptchaConfig } from '../lib/types';
import { Turnstile } from '../components/Turnstile';
import { Button, Card, Field, Input } from '../components/ui';

/**
 * The frame the three account-recovery screens share with the sign-in page.
 *
 * Its own small component rather than a shared layout: these pages are reached by people
 * who cannot get in, sometimes from a link in an email on a phone, and the one thing they
 * all need is to look like the same product as the page they were trying to reach.
 */
function AuthCard({
  title, subtitle, children, footer,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <div className="grid min-h-full place-items-center p-6">
      <Card className="w-full max-w-sm p-6">
        <div className="mb-5 flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-md
                           bg-[var(--ff-primary)] font-semibold text-[var(--ff-on-primary)]">
            F
          </span>
          <div>
            <h1 className="text-base font-semibold">{title}</h1>
            <p className="text-xs text-[var(--ff-text-muted)]">{subtitle}</p>
          </div>
        </div>

        {children}

        {footer && (
          <p className="mt-4 text-center text-xs text-[var(--ff-text-muted)]">{footer}</p>
        )}
      </Card>
    </div>
  );
}

function Alert({ tone, children }: { tone: 'error' | 'ok'; children: React.ReactNode }) {
  return (
    <p
      role={tone === 'error' ? 'alert' : 'status'}
      className={tone === 'error'
        ? `rounded-md border border-[var(--ff-danger)] bg-[var(--ff-danger-soft)]
           px-3 py-2 text-sm text-[var(--ff-danger)]`
        : `rounded-md border border-[var(--ff-border)] bg-[var(--ff-surface-raised)]
           px-3 py-2 text-sm`}
    >
      {children}
    </p>
  );
}

/**
 * Asks for a reset link.
 *
 * The server answers identically for an address it knows and one it does not, and this
 * screen is careful not to undo that: on success it shows one sentence that is true either
 * way, and it never reports "no account with that address" — which is the whole point.
 */
export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [captcha, setCaptcha] = useState<AuthCaptchaConfig | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaNonce, setCaptchaNonce] = useState(0);
  const [challenged, setChallenged] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.auth.captcha()
      .then((config) => { if (!cancelled) setCaptcha(config); })
      .catch(() => { if (!cancelled) setCaptcha(null); });
    return () => { cancelled = true; };
  }, []);

  const enabled = captcha !== null && captcha.mode !== 'off' && captcha.siteKey.length > 0;
  const showCaptcha = enabled && (captcha.mode === 'always' || challenged);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      await api.auth.forgotPassword({
        email,
        captchaToken: captchaToken ?? undefined,
      });

      setSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'captcha_required') {
        setChallenged(true);
        setError('Complete the verification check below, then try again.');
      } else {
        // Only transport and rate-limit failures reach here: the endpoint answers 202 for
        // every address it is given, so there is no "unknown account" case to report.
        setError(err instanceof ApiError
          ? err.message
          : 'Could not reach the server. Try again in a moment.');
      }
    } finally {
      setCaptchaToken(null);
      setCaptchaNonce((n) => n + 1);
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthCard
        title="Check your email"
        subtitle="If we know that address, a link is on its way."
        footer={<Link className="text-[var(--ff-primary)]" to="/login">Back to sign in</Link>}
      >
        {/* The condition leads, because tacking "if there is an account" onto the end of a
            sentence that has already claimed to send something reads as a hedge. */}
        <Alert tone="ok">
          If there is an account for <strong>{email}</strong>, a link is on its way to it.
          The link works once and expires in an hour.
        </Alert>

        {/* Deliberately offered rather than hidden: the commonest reason somebody is still
            on this screen is a typo in the address, and the second is a slow inbox. */}
        <Button
          className="mt-3 w-full"
          onClick={() => { setSent(false); }}
        >
          Use a different address
        </Button>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Reset your password"
      subtitle="We will email you a link."
      footer={<Link className="text-[var(--ff-primary)]" to="/login">Back to sign in</Link>}
    >
      <form onSubmit={submit} className="space-y-3" noValidate>
        {error && <Alert tone="error">{error}</Alert>}

        <Field label="Email" required>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
            required
          />
        </Field>

        {showCaptcha && captcha && (
          <div className="pt-1">
            <Turnstile
              siteKey={captcha.siteKey}
              onToken={setCaptchaToken}
              resetKey={captchaNonce}
            />
          </div>
        )}

        <Button type="submit" variant="primary" className="w-full" disabled={busy}>
          {busy ? 'Sending…' : 'Email me a link'}
        </Button>
      </form>
    </AuthCard>
  );
}

/**
 * Spends the link and sets the new password.
 *
 * Does not sign anybody in afterwards, matching the server: a link in an email that ends
 * in a session is a forwarded email that ends in a session. It sends them to sign in with
 * what they have just chosen, which also makes them type it a second time.
 */
export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Checked in the browser only to save a round trip; the server enforces the same rule
  // and is the one that decides.
  const tooShort = password.length > 0 && password.length < 12;
  const mismatch = confirm.length > 0 && confirm !== password;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError('Those two passwords are not the same.');
      return;
    }

    setBusy(true);

    try {
      await api.auth.resetPassword({ token, password });

      navigate('/login', {
        replace: true,
        state: { notice: 'Your password has been changed. Sign in with it.' },
      });
    } catch (err) {
      setError(err instanceof ApiError
        ? err.message
        : 'Could not reach the server. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthCard
        title="That link is incomplete"
        subtitle="It is missing the part that identifies you."
        footer={<Link className="text-[var(--ff-primary)]" to="/forgot-password">Ask for a new link</Link>}
      >
        <Alert tone="error">
          Open the link from the email itself rather than typing the address. Some mail
          clients cut long links in half.
        </Alert>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Choose a new password"
      subtitle="This link works once."
      footer={<Link className="text-[var(--ff-primary)]" to="/login">Back to sign in</Link>}
    >
      <form onSubmit={submit} className="space-y-3" noValidate>
        {error && <Alert tone="error">{error}</Alert>}

        <Field
          label="New password"
          required
          // Length over composition rules, per NIST SP 800-63B (docs/10 §5).
          hint="At least 12 characters. Length matters more than symbols."
          error={tooShort ? 'At least 12 characters.' : undefined}
        >
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={12}
            autoFocus
            required
          />
        </Field>

        <Field
          label="Repeat it"
          required
          error={mismatch ? 'These do not match.' : undefined}
        >
          <Input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />
        </Field>

        <Button
          type="submit"
          variant="primary"
          className="w-full"
          disabled={busy || tooShort || mismatch || password.length === 0}
        >
          {busy ? 'Saving…' : 'Change my password'}
        </Button>
      </form>
    </AuthCard>
  );
}

/**
 * Confirms an address from the link in the registration email.
 *
 * The email has been sending people here since verification was added and nothing was
 * listening, so the link went to a blank 404 and the address stayed unconfirmed.
 */
export function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [state, setState] = useState<'working' | 'done' | 'failed'>(
    token ? 'working' : 'failed');
  const [message, setMessage] = useState<string | null>(
    token ? null : 'That link is missing the part that identifies you.');

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    api.auth.verifyEmail({ token })
      .then(() => { if (!cancelled) setState('done'); })
      .catch((err) => {
        if (cancelled) return;
        setState('failed');
        setMessage(err instanceof ApiError
          ? err.message
          : 'Could not reach the server. Try the link again in a moment.');
      });

    return () => { cancelled = true; };
  }, [token]);

  return (
    <AuthCard
      title={state === 'done' ? 'Address confirmed' : 'Confirming your address'}
      subtitle={state === 'done' ? 'That is everything.' : 'One moment.'}
      footer={<Link className="text-[var(--ff-primary)]" to="/login">Go to sign in</Link>}
    >
      {state === 'working' && <Alert tone="ok">Checking your link…</Alert>}
      {state === 'done' && (
        <Alert tone="ok">Thank you — we can reach you at this address now.</Alert>
      )}
      {state === 'failed' && <Alert tone="error">{message}</Alert>}
    </AuthCard>
  );
}
